import React, { useEffect, useState } from "react";
import { teluguImage } from "../lib/teluguImage";
import CounterShiftFilter, { scopeLabel, useCounterShiftMeta } from "../components/CounterShiftFilter";
import api, { fmtErr } from "../lib/api";
import { printHtml } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import StatusBadge, { sareeStatusLabel } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { FileDown, Printer, Landmark, Flame, FileText } from "lucide-react";
import LetterViewer from "../components/LetterViewer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";

// Sale receipt date (IST) for sold tokens.
const saleDate = (r) => (r.sale?.sold_at ? new Date(r.sale.sold_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "-");

export default function SareeReports() {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [temple, setTemple] = useState(null);
  const [brandLogo, setBrandLogo] = useState("");
  const logo = temple?.logo || brandLogo;
  const [f, setF] = useState({ frm: "", to: "", category: "", saree_type_id: "", status: "", shift_id: "", counter_id: "" });
  const csMeta = useCounterShiftMeta();
  const [data, setData] = useState(null);
  const [indentOf, setIndentOf] = useState(null); // token whose indent copy is open
  const canSeeIndent = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role);
  const reportRef = React.useRef(null);

  const load = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/sarees/report?${q}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => {
    load();
    api.get("/saree-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/saree-categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/temples").then((r) => setTemple(r.data.find((t) => t.temple_id === user.temple_id))).catch(() => {});
    api.get("/public/branding").then((r) => setBrandLogo(r.data.logo || "")).catch(() => {});
  }, []); // eslint-disable-line

  const downloadPdf = async () => {
    if (!data || data.rows.length === 0) { toast.error("No data to export"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    if (logo) { try { doc.addImage(logo, /^data:image\/jpe?g/i.test(logo) ? "JPEG" : "PNG", 12, 8, 20, 20); } catch { /* skip */ } }
    const right = temple?.logo_right;
    if (right) { try { doc.addImage(right, /^data:image\/jpe?g/i.test(right) ? "JPEG" : "PNG", 297 - 32, 8, 20, 20); } catch { /* skip */ } }
    doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    if (temple?.name_telugu) {
      const tel = await teluguImage(temple.name_telugu);
      const w = Math.min(7 * tel.ratio, 200);
      doc.addImage(tel.data, "PNG", 148 - w / 2, 3.2, w, w / tel.ratio);
      doc.setFont("helvetica", "bold"); doc.setFontSize(14);
    }
    doc.text(temple?.name || "All Temples", 148, temple?.name_telugu ? 15.5 : 15, { align: "center" });
    doc.setFontSize(10);
    doc.text("Saree Details Report" + scopeLabel(user, csMeta, f).replace(/·/g, "-"), 148, 22, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleString("en-IN")}`, 148, 27, { align: "center" });
    autoTable(doc, {
      startY: 32,
      head: [["Sl No", "Token No", "Date", "Devotee Name", "Category", "Saree Type", "Rate", "Phone", "Status", "Sanction", "Out Type", "Indent No", "Sale Rect No", "Sale Date"]],
      body: data.rows.map((r, i) => [
        i + 1, r.token_no, new Date(r.created_at).toLocaleDateString("en-IN"), r.name || "-", r.category, r.saree_type_name,
        `Rs ${r.rate}`, r.phone || "-", sareeStatusLabel(r.status), r.sanction_amount ? `Rs ${r.sanction_amount}` : "-",
        r.out_type || "-", r.indent_no || "-", r.sale?.rect_no || "-", saleDate(r),
      ]),
      foot: [["", "", "", "", "", "Total", `Rs ${data.totals.rate_total}`, `${data.totals.count} tokens`, "", `Rs ${data.totals.sanction_total}`, "", "", "", ""]],
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [128, 0, 32], textColor: 255 },
      footStyles: { fillColor: [253, 246, 227], textColor: [128, 0, 32], fontStyle: "bold" },
    });
    doc.setPage(doc.getNumberOfPages());
    // Signature block and footer, same as the protocol report PDFs.
    const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight();
    const signs = [temple?.sign_label_1 || "Counter Signature", temple?.sign_label_2 || "Superintendent", temple?.sign_label_3 || "Executive Officer"];
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setDrawColor(90);
    signs.forEach((label, i) => { const x = [W / 6, W / 2, (W * 5) / 6][i]; doc.line(x - 30, H - 25, x + 30, H - 25); doc.text(label, x, H - 20, { align: "center" }); });
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.text("Aatreya Infotech Systems - Temple ERP", W / 2, H - 7, { align: "center" });
    doc.save(`SareeReport_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div data-testid="saree-reports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-5">Saree Details Report</h1>
      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="saree-report-from" /></div>
          <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="saree-report-to" /></div>
          <div><Label className="text-xs">Category Type</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="saree-report-category"><SelectValue placeholder="Select All" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Select All</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Saree Type</Label>
            <Select value={f.saree_type_id} onValueChange={(v) => setF({ ...f, saree_type_id: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="saree-report-type"><SelectValue placeholder="Select All" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Select All</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="saree-report-status"><SelectValue placeholder="Select All" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Select All</SelectItem>
                <SelectItem value="ISSUED">Receiving</SelectItem><SelectItem value="GODOWN_IN">Godown In</SelectItem><SelectItem value="GODOWN_OUT">Godown Out</SelectItem><SelectItem value="SOLD">Sold</SelectItem>
              </SelectContent>
            </Select></div>
          <CounterShiftFilter user={user} meta={csMeta} value={f} onChange={setF} testid="saree-report" />
          <div className="flex gap-2">
            <Button onClick={load} data-testid="saree-report-run" className="bg-maroon-700 hover:bg-maroon-800 text-white">View Report</Button>
            <Button onClick={downloadPdf} variant="outline" title="Download PDF" data-testid="saree-report-pdf"><FileDown className="w-4 h-4" /></Button>
            <Button onClick={() => reportRef.current && printHtml(reportRef.current.outerHTML, "a4-page", `Saree_Report_${new Date().toLocaleDateString("en-CA")}`)} variant="outline" title="Print" data-testid="saree-report-print"><Printer className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div ref={reportRef} className="print-area print-a4 bg-white border border-[#E5D9C5] rounded-md shadow-sm p-6" data-testid="saree-report-table-wrap">
          <div className="flex items-center gap-3 border-b-2 border-maroon-800 pb-3 mb-4">
            {logo ? <img src={logo} alt="logo" className="w-14 h-14 object-contain" /> : <Landmark className="w-9 h-9 text-maroon-800" />}
            <div className="flex-1 text-center">
              {temple?.name_telugu && <p className="font-telugu text-2xl text-maroon-800 leading-tight">{temple.name_telugu}</p>}
              <h2 className="font-heading text-xl font-bold text-maroon-900">{temple?.name || "All Temples"}</h2>
              <p className="text-xs uppercase tracking-widest text-gold-700">Saree Details Report</p>
              <p className="text-xs text-slate-500">Generated: {new Date().toLocaleString("en-IN")}</p>
            </div>
            {temple?.logo_right ? <img src={temple.logo_right} alt="logo" className="w-14 h-14 object-contain" />
              : logo ? <div className="w-14 h-14" /> : <Flame className="w-9 h-9 text-maroon-800" />}
          </div>
          <table className="w-full text-sm" data-testid="saree-report-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
                <th className="py-2 pr-3">Sl No</th><th className="py-2 pr-3">Token No</th><th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Devotee Name</th><th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Saree Type</th><th className="py-2 pr-3 text-right">Rate</th>
                <th className="py-2 pr-3">Phone</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3 text-right">Sanction</th>
                <th className="py-2 pr-3">Out Type</th><th className="py-2 pr-3">Indent No</th>
                <th className="py-2 pr-3">Sale Rect No</th><th className="py-2">Sale Date</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={14} className="py-8 text-center text-slate-400">No records</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={r.token_no} className="border-b border-[#F1EADB]" data-testid={`saree-report-row-${r.token_no}`}>
                  <td className="py-2 pr-3">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.token_no}</td>
                  <td className="py-2 pr-3 text-xs">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="py-2 pr-3 font-semibold">{r.name || "-"}</td>
                  <td className="py-2 pr-3">{r.category}</td>
                  <td className="py-2 pr-3">{r.saree_type_name}</td>
                  <td className="py-2 pr-3 text-right">₹{r.rate}</td>
                  <td className="py-2 pr-3">{r.phone || "-"}</td>
                  <td className="py-2 pr-3"><StatusBadge status={r.status} saree /></td>
                  <td className="py-2 pr-3 text-right">{r.sanction_amount ? `₹${r.sanction_amount}` : "-"}</td>
                  <td className="py-2 pr-3">{r.out_type || "-"}</td>
                  <td className="py-2 pr-3">{r.indent_no || "-"}
                    {r.indent_file_id && canSeeIndent && (
                      <button type="button" onClick={() => setIndentOf(r)} className="print:hidden ml-1 inline-flex items-center gap-0.5 text-xs text-maroon-700 underline" title="View indent copy" data-testid={`saree-report-indent-${r.token_no}`}>
                        <FileText className="w-3.5 h-3.5" />View</button>
                    )}</td>
                  <td className="py-2 pr-3 font-semibold" data-testid={`saree-report-rect-${r.token_no}`}>{r.sale?.rect_no || "-"}</td>
                  <td className="py-2 text-xs">{saleDate(r)}</td>
                </tr>
              ))}
              {data.rows.length > 0 && (
                <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold" data-testid="saree-report-totals">
                  <td colSpan={6} className="py-2 pr-3">Total ({data.totals.count} tokens)</td>
                  <td className="py-2 pr-3 text-right">₹{data.totals.rate_total.toLocaleString("en-IN")}</td>
                  <td colSpan={2} />
                  <td className="py-2 pr-3 text-right">₹{data.totals.sanction_total.toLocaleString("en-IN")}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex justify-between mt-12 text-xs text-slate-600" data-testid="saree-report-signatures">
            {[temple?.sign_label_1 || "Counter Signature", temple?.sign_label_2 || "Superintendent", temple?.sign_label_3 || "Executive Officer"].map((l) => (
              <div key={l} className="text-center"><p className="border-t border-slate-400 pt-1 px-6 font-semibold">{l}</p></div>))}
          </div>
          <p className="text-center text-[11px] text-slate-500 mt-6">Aatreya Infotech Systems - Temple ERP</p>
        </div>
      )}
      <Dialog open={!!indentOf} onOpenChange={(o) => !o && setIndentOf(null)}>
        <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto" data-testid="indent-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Indent Copy · {indentOf?.indent_no || indentOf?.token_no}</DialogTitle></DialogHeader>
          {indentOf && <LetterViewer autoLoad path={`/sarees/indent-file/${indentOf.indent_file_id}`} label="Indent Copy" roles={["SUPER_ADMIN", "TEMPLE_ADMIN"]} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
